import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { FaArrowRight } from 'react-icons/fa';
import './ProjectsPage.css';

const fullExperiences = [
  {
    title: 'Samsung Research Institute, Noida',
    type: 'Internship',
    date: 'May ’26 – Jul ’26',
    topics: ['On-device AI', 'TFLite', 'Model Optimization'],
    description:
      'Built an on-device document classification system for Android, trained a lightweight classifier, and optimized it for TFLite Runtime with under-80ms CPU inference latency and an F1 score of 0.997. Also reduced peak RAM usage of a production LSTM model by 80% from 1.5GB to 300MB.',
  },
  {
    title: 'Indic OCR',
    type: 'Research Experience',
    date: '2024 – 2025',
    link: 'https://drive.google.com/drive/folders/1tU5Maypg5wYxv1PiJ34zuHa_OoLo1xbX?usp=sharing',
    topics: ['OCR', 'Computer Vision', 'Indic Languages'],
    description:
      'Worked on OCR for Devanagari and legacy non-Unicode fonts, using synthetic data to better handle ligatures and conjuncts and contributing to a language-agnostic design for future extension to other Indic scripts.',
  },
  {
    title: 'ULM Framework',
    type: 'Research Experience',
    date: '2025 – Present',
    link: 'https://github.com/nikhil-405/DeepMotionTrack/tree/main',
    topics: ['Medical Imaging', 'Ultrasound', 'Deep Learning'],
    description:
      'Worked on attention-based denoising and motion-guided assignment for robust microbubble tracking in ULM, helping achieve state-of-the-art vessel reconstruction quality at half the imaging rate.',
  },
];

export default function ExperiencesPage() {
  const [query, setQuery] = useState('');

  const filteredExperiences = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return fullExperiences;
    return fullExperiences.filter((exp) => {
      const haystack = [exp.title || '', exp.type || '', exp.date || '', exp.description || '', ...(exp.topics || [])]
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [query]);

  return (
    <>
      <Helmet>
        <title>Experience | Nikhil Goyal | AI Internship & Research</title>
        <meta name="description" content="Explore Nikhil Goyal’s experience in AI research and internships, including Samsung, Indic OCR, and ULM-based medical imaging work." />
        <meta name="keywords" content="Nikhil Goyal experience, Samsung internship, AI internship, Indic OCR, ULM research, medical imaging AI, on-device AI, IIT Gandhinagar" />
        <meta name="author" content="Nikhil Goyal" />
        <link rel="canonical" href="https://nikhil-405.github.io/experience" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Experience | Nikhil Goyal | AI Internship & Research" />
        <meta property="og:description" content="Experience page highlighting Nikhil Goyal’s AI internship and research work in on-device AI, OCR, and medical imaging." />
        <meta property="og:url" content="https://nikhil-405.github.io/experience" />
        <meta property="og:type" content="website" />
        <meta name="twitter:title" content="Experience | Nikhil Goyal | AI Internship & Research" />
        <meta name="twitter:description" content="Experience page highlighting Nikhil Goyal’s AI internship and research work in on-device AI, OCR, and medical imaging." />
      </Helmet>

      <main className="projects-page">
        <h2 className="projects-page-title">All Experience</h2>

        <div className="projects-search" role="search">
          <input
            type="text"
            className="projects-search-input"
            placeholder="Search by title, type, or description..."
            aria-label="Search experience"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {query && (
            <button
              type="button"
              className="projects-search-clear"
              onClick={() => setQuery('')}
              aria-label="Clear search"
              title="Clear"
            >
              Clear
            </button>
          )}
        </div>

        <div className="project-list">
          {filteredExperiences.map((exp) => {
            const cardBody = (
              <>
                {exp.link && <FaArrowRight className="project-card-arrow" />}
                <h3>{exp.title}</h3>
                <span className="project-type">{exp.type}</span>
                <p className="project-date">{exp.date}</p>
                <p>{exp.description}</p>
                <div className="topic-tags">
                  {exp.topics.map((topic, index) => (
                    <span key={index} className="topic-tag">
                      {topic}
                    </span>
                  ))}
                </div>
              </>
            );

            if (exp.link) {
              return (
                <a
                  key={exp.title}
                  href={exp.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="project-card"
                >
                  {cardBody}
                </a>
              );
            }

            return (
              <div key={exp.title} className="project-card project-card-no-link">
                {cardBody}
              </div>
            );
          })}
          {filteredExperiences.length === 0 && (
            <div className="projects-empty">No experience matches your search.</div>
          )}
        </div>
      </main>
    </>
  );
}
